import React, { useState ,useEffect} from 'react';
import styled from 'styled-components';
import { Star, Clock, Users, BookOpen, ShoppingCart, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { loadCourseDetail } from './courseDetailData';
// 스타일드 컴포넌트 정의
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BreadcrumbNav = styled.nav`
  margin-bottom: 1.5rem;
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  
  &:after {
    content: '/';
    margin-left: 0.5rem;
    color: #94a3b8;
  }
  
  &:last-child:after {
    content: none;
  }
`;

const BreadcrumbLink = styled.a`
  color: #64748b;
  text-decoration: none;
  
  &:hover {
    color: #0f172a;
    text-decoration: underline;
  }
`;

const CourseHeader = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const CourseImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CourseCategory = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: ${props => props.bgColor || '#3b82f6'};
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CourseTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const CourseDescription = styled.p`
  color: #475569;
  line-height: 1.6;
  margin: 0;
`;

const CourseStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #64748b;
`;

const PriceCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PriceValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
`;

const PriceDiscount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #94a3b8;
  font-size: 1rem;
`;

const DiscountBadge = styled.span`
  background-color: #fee2e2;
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background-color: #0f172a;
    color: white;
    border: none;
    
    &:hover {
      background-color: #1e293b;
    }
  ` : `
    background-color: white;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background-color: #f8fafc;
      border-color: #cbd5e1;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CourseDetails = styled.div`
  display: grid;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const TabsContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const TabsList = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 1rem;
  background-color: ${props => props.active ? '#f8fafc' : 'white'};
  border: none;
  border-bottom: 2px solid ${props => props.active ? '#0f172a' : 'transparent'};
  color: ${props => props.active ? '#0f172a' : '#64748b'};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f8fafc;
  }
`;

const TabContent = styled.div`
  padding: 1.5rem;
  background-color: white;
`;

const CurriculumList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CurriculumSection = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  overflow: hidden;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem;
  background-color: #f8fafc;
  border: none;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background-color: #f1f5f9;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
`;

const SectionContent = styled.div`
  padding: ${props => props.isOpen ? '1rem' : '0'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s;
`;

const LessonList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LessonItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
`;

const LessonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background-color: #e2e8f0;
  border-radius: 9999px;
  color: #64748b;
`;

const LessonTitle = styled.span`
  flex: 1;
  font-size: 0.875rem;
  color: #0f172a;
`;

const LessonDuration = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const InstructorCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const InstructorHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
`;

const InstructorTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`;

const InstructorContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InstructorProfile = styled.div`
  display: flex;
  gap: 1rem;
`;

const InstructorAvatar = styled.img`
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  object-fit: cover;
`;

const InstructorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InstructorName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
`;

const InstructorRole = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
`;

const InstructorBio = styled.p`
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
`;

// 강의 상세 페이지 컴포넌트
function CourseDetail() {
  const navigate = useNavigate(); // 페이지 이동을 위한 훅 추가
  const { productId } = useParams(); // URL에서 productId 파라미터 가져오기
  const [activeTab, setActiveTab] = useState('curriculum');
  const [openSections, setOpenSections] = useState([0]);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
    // 강의 수정 페이지로 이동하는 함수
    const goToEditPage = () => {
      navigate(`/edit?prod_id=${productId}`);
    };
    // 기본 소개를 먼저 보여 주고, 강좌 부속 정보는 독립적으로 보완합니다.
    useEffect(() => {
      const controller = new AbortController();
      setLoading(true);
      setCourse(null);
      setActiveTab('curriculum');
      setOpenSections([0]);
      loadCourseDetail(productId, {
        signal: controller.signal,
        onBase: base => {
          if (controller.signal.aborted) return;
          setCourse(base);
          setLoading(false);
          if (base.isService) setActiveTab('description');
        }
      }).then(result => {
        if (controller.signal.aborted) return;
        setCourse(result);
        if (!result.curriculum.length) setActiveTab(tab => tab === 'curriculum' ? 'description' : tab);
      }).catch(error => {
        if (!controller.signal.aborted) console.error('기본 정보 로딩 실패:', error.message);
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
      return () => controller.abort();
    }, [productId]);
  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return "정보 없음";
    const date = new Date(dateString);
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
  };
  


  
  const toggleSection = (index) => {
    if (openSections.includes(index)) {
      setOpenSections(openSections.filter(i => i !== index));
    } else {
      setOpenSections([...openSections, index]);
    }
  };
    
  if (loading) {
    return <Container>정보를 불러오는 중입니다...</Container>;
  }
  
  if (!course) {
    return <Container>정보를 찾을 수 없습니다.</Container>;
  }
  
  return (
    <Container>
      <BreadcrumbNav>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/">홈</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink href={`/course-library/${course.type_id}`}>{course.category}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink href="#">{course.title}</BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </BreadcrumbNav>
      {!course.isService && <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={goToEditPage} style={{ backgroundColor: '#4b5563', color: 'white' }}>
          강의 수정하기
        </Button>
      </div>}
      <CourseHeader>
        <div>
          <CourseImageContainer>
            <CourseImage src={course.image} alt={course.title} />
            <CourseCategory bgColor={course.categoryColor}>{course.category}</CourseCategory>
          </CourseImageContainer>
          
          <CourseInfo>
            <CourseTitle>{course.title}</CourseTitle>
            <CourseDescription>{course.description}</CourseDescription>
            
            <CourseStats>
              {course.rating !== null && <StatItem>
                <Star size={16} fill="#f59e0b" color="#f59e0b" />
                <span>{course.rating}</span>
              </StatItem>}
              {course.students !== null && <StatItem>
                <Users size={16} />
                <span>{course.students.toLocaleString()}명 수강</span>
              </StatItem>}
              <StatItem>
                <Clock size={16} />
                <span>{course.duration}</span>
              </StatItem>
              <StatItem>
                <BookOpen size={16} />
                <span>최근 업데이트: {course.lastUpdated}</span>
              </StatItem>
            </CourseStats>
          </CourseInfo>
        </div>
        
        <PriceCard>
          <div>
            <PriceValue>{course.price === null ? '가격 정보 없음' : `${course.price.toLocaleString()}원`}</PriceValue>
            {course.originalPrice !== null && course.discount !== null && <PriceDiscount>
              <OriginalPrice>{course.originalPrice.toLocaleString()}원</OriginalPrice>
              <DiscountBadge>{course.discount}% 할인</DiscountBadge>
            </PriceDiscount>}
          </div>
          
          <ButtonGroup>
            <Button>
              <ShoppingCart size={16} />
              장바구니
            </Button>
            <Button primary>
              <CreditCard size={16} />
              바로 구매하기
            </Button>
          </ButtonGroup>
        </PriceCard>
      </CourseHeader>
      
      <CourseDetails>
        <TabsContainer>
          <TabsList>
            {!course.isService && <TabButton
              active={activeTab === 'curriculum'} 
              onClick={() => setActiveTab('curriculum')}
            >
              커리큘럼
            </TabButton>}
            <TabButton 
              active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              상세 설명
            </TabButton>
            {!course.isService && <TabButton
              active={activeTab === 'reviews'} 
              onClick={() => setActiveTab('reviews')}
            >
              수강평
            </TabButton>}
          </TabsList>
          
          <TabContent>
            {course.supplementaryUnavailable && <p role="status">일부 상세 정보를 불러오지 못했습니다. 기본 소개는 계속 확인할 수 있습니다.</p>}
            {activeTab === 'curriculum' && (
              <CurriculumList>
                {course.curriculum.map((section, index) => (
                  <CurriculumSection key={index}>
                    <SectionHeader onClick={() => toggleSection(index)}>
                      <SectionTitle>{section.title}</SectionTitle>
                      {openSections.includes(index) ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </SectionHeader>
                    <SectionContent isOpen={openSections.includes(index)}>
                      <LessonList>
                        {section.lessons.map((lesson, lessonIndex) => (
                          <LessonItem key={lessonIndex}>
                            <LessonIcon>
                              <BookOpen size={12} />
                            </LessonIcon>
                            <LessonTitle>{lesson.title}</LessonTitle>
                            <LessonDuration>{lesson.duration}</LessonDuration>
                          </LessonItem>
                        ))}
                      </LessonList>
                    </SectionContent>
                  </CurriculumSection>
                ))}
              </CurriculumList>
            )}
            
            {activeTab === 'description' && (
            <div>{course.descriptionIsHtml ? <div dangerouslySetInnerHTML={{ __html: course.detailedDescription }} /> : <p style={{ whiteSpace: 'pre-wrap' }}>{course.detailedDescription}</p>}</div>
          )}
            
            {activeTab === 'reviews' && (
        <div>
          {course.reviews && course.reviews.length > 0 ? (
            course.reviews.map((review, index) => (
              <div key={index} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>평점: {review.rating}</div>
                  <div>{formatDate(review.created_at)}</div>
                </div>
                <div>{review.comment}</div>
              </div>
            ))
          ) : (
            <p>아직 수강평이 없습니다.</p>
          )}
        </div>
      )}
          </TabContent>
        </TabsContainer>
        
        <InstructorCard>
          <InstructorHeader>
            <InstructorTitle>{course.isService ? '제공자 소개' : '강사 소개'}</InstructorTitle>
          </InstructorHeader>
          <InstructorContent>
            <InstructorProfile>
              {course.instructor.avatar ? <InstructorAvatar src={course.instructor.avatar} alt={course.instructor.name} /> : <InstructorAvatar as="div" aria-hidden="true" />}
              <InstructorInfo>
                <InstructorName>{course.instructor.name}</InstructorName>
                <InstructorRole>{course.instructor.role}</InstructorRole>
              </InstructorInfo>
            </InstructorProfile>
            <InstructorBio>{course.instructor.bio}</InstructorBio>
          </InstructorContent>
        </InstructorCard>
      </CourseDetails>
    </Container>
  );
}

export default CourseDetail;
